import { useBreakpointValue, Flex, Text } from '@chakra-ui/react';
import { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useRequestSetupIntentMutation, useAddSetupIntentMutation, usePayClaimMutation } from 'redux/api/ciosUsersApi/ciosUsersApi';
import { isEmpty } from 'common/utils/isEmpty';
import useUserTracker from 'common/utils/user-tracker/useUserTracker';
import ContactCard from 'components/shared/contact-card';
import ClaimsPaymentSummaryCard from 'components/claims/claims-payment-summary';
import ApplyButtons from 'components/apply-shared/apply-buttons';
import ApplyTitle from 'components/apply-shared/apply-title';
import ApplyContent from 'components/apply-shared/apply-content';
import { selectUser } from 'redux/features/auth/authSlice';
import EmptyPage from 'common/components/empty-page';
import { FaCreditCard } from 'react-icons/fa6';
import { ApplyFormLayout, ApplyFormContainer } from 'common/layouts/apply-form';
import Payment from 'components/stripe/payment';
import FinishSetupIntent from 'components/stripe/finish-setup-intent';
import { TabGroup } from 'common/components/tab';
import { SetupCard } from 'common/components/setup-card';

const Step2Payment = ({ claim, claimId, onBackClick, onPaymentComplete }) => {
  const { trackPageLoad, trackCustomEvent } = useUserTracker();

  useEffect(() => {
    trackPageLoad('ClaimPayment');
  }, []);

  // STATES
  const [clientSecret, setSecret] = useState();
  const [isPaymentSuccess, setPaymentSuccess] = useState(false);
  const [localSetupIntentID, setLocalSetupIntentID] = useState(null);
  const [isPaymentElementLoading, setIsPaymentElementLoading] = useState(true);
  const [showNewCard, setShowNewCard] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Ref for external form submit
  const paymentRef = useRef(null);

  // Determine if we're on md+ breakpoint
  const isDesktop = useBreakpointValue({ base: false, md: true });

  // HOOKS
  const [requestSetupIntent] = useRequestSetupIntentMutation();
  const [addSetupIntent] = useAddSetupIntentMutation();
  const [payClaim, { isLoading: payClaimLoading }] = usePayClaimMutation();
  const [searchParams, setSearchParams] = useSearchParams();
  const setupIntent = searchParams.get('setup_intent');

  // Retrieve user details
  const userDetails = useSelector(selectUser);

  // SIDE EFFECTS
  useEffect(() => {
    requestSetupIntent()
      .unwrap()
      .then((response) => {
        setSecret(response?.data?.clientSecret);
      })
      .catch((error) => {
        toast.error(error?.message || 'Failed to setup intent!');
      });
  }, [requestSetupIntent]);

  useEffect(() => {
    if (clientSecret && isEmpty(setupIntent)) {
      setIsPaymentElementLoading(true);
    } else {
      setIsPaymentElementLoading(false);
    }
  }, [clientSecret, setupIntent]);

  useEffect(() => {
    if (setupIntent) {
      addSetupIntent({ setupIntentID: setupIntent, makeDefault: true })
        .unwrap()
        .then(() => {
          // Clear the setup_intent from URL after successful processing
          searchParams.delete('setup_intent');
          setSearchParams(searchParams, { replace: true });
          setLocalSetupIntentID(setupIntent);
        })
        .catch((error) => {
          toast.error(error.message, { toastId: 'setupIntentFailed' });
          console.error(error);
        });
    }
  }, [setupIntent, searchParams, setSearchParams, addSetupIntent]);

  const handlePaymentElementReady = () => {
    setIsPaymentElementLoading(false);
  };

  const handleSetupSuccess = (id) => {
    setLocalSetupIntentID(id);
    // Automatically process the payment after adding the payment method
    handlePaymentComplete();
  };

  const handlePayWithSavedMethod = () => {
    handlePaymentComplete();
  };

  const handlePaymentComplete = () => {
    setIsProcessing(true);
    payClaim({ claimId: claimId })
      .unwrap()
      .then(() => {
        if (onPaymentComplete) {
          onPaymentComplete();
        }
      })
      .catch((error) => {
        setIsProcessing(false);
        toast.error(
          error.message || 'Failed to complete payment and finalize claim'
        );
      });
  };

  // Mobile payment handler
  const handleMobilePayment = () => {
    if (showNewCard) {
      // If adding new payment method, submit the payment form
      paymentRef.current?.submit();
    } else {
      // If using saved payment method, process payment directly
      handlePayWithSavedMethod();
    }
  };

  // Tab configuration
  const tabs = [
    {
      title: 'Use Saved Payment Method',
      selected: !showNewCard,
      onClick: () => setShowNewCard(false),
    },
    {
      title: 'Add New Payment Method',
      selected: showNewCard,
      onClick: () => setShowNewCard(true),
    },
  ];

  // Show processing state
  if (isProcessing) {
    return (
      <div className='flex flex-col w-full h-full'>
        <ApplyTitle
          title='Finalize Claim Resolution'
          subtitle='Complete your claim payment to finalize the resolution.'
        />

        <ApplyContent>
          <ApplyFormContainer>
            <div className='flex justify-center items-center min-h-[400px]'>
              <SetupCard
                isLoading
                title='Processing your payment'
                subtitle='Please do not refresh the page'
                message='Please wait while we process your payment. Do not refresh the page.'
              />
            </div>
          </ApplyFormContainer>
        </ApplyContent>
      </div>
    );
  }

  return (
    <div className='flex flex-col w-full h-full'>
      <ApplyTitle
        title='Pay Claim'
        subtitle='Complete your claim payment to finalize the resolution.'
        buttons={
          <ApplyButtons
            onBackClick={onBackClick}
            backTitle='Back to Claim Details'
            onNextClick={!isDesktop ? handleMobilePayment : undefined}
            nextTitle={!isDesktop ? 'Complete Payment' : undefined}
            isValid={!isDesktop}
            moveOn={!isDesktop}
            loading={!isDesktop && payClaimLoading}
          />
        }
      />

      <ApplyContent>
        <ApplyFormContainer>
          <div className='grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-8 w-full grid-flow-dense justify-items-center'>
            {/* Payment summary card */}
            <div className='w-full col-start-1 md:col-start-2 flex flex-col gap-4 md:sticky md:top-0 md:self-start'>
              <ClaimsPaymentSummaryCard
                onClick={
                  isDesktop && !showNewCard ? handlePayWithSavedMethod : 
                  isDesktop && showNewCard ? () => paymentRef.current?.submit() : undefined
                }
                isLoading={
                  isDesktop && showNewCard && (isPaymentElementLoading || !clientSecret) || payClaimLoading
                }
                claim={claim}
                showSavings={false}
              />
            </div>

            {/* Payment component */}
            <div className='w-full relative'>
              {/* Payment Method Tabs */}
              <div className='flex flex-col gap-4 mb-6'>
                <TabGroup tabs={tabs} />
              </div>

              {/* Payment Form - Only show when adding new payment method */}
              {showNewCard && (
                <>
                  {!clientSecret ? (
                    <EmptyPage
                      isLoading={true}
                      title='Loading Payment'
                      subtitle='Please wait while we load your payment method.'
                    />
                  ) : !isEmpty(setupIntent) || localSetupIntentID ? (
                    <FinishSetupIntent
                      removeSetupIntent={() => {
                        searchParams.delete('setup_intent');
                        setSearchParams(searchParams, { replace: true });
                        setLocalSetupIntentID(null);
                      }}
                      onNextClick={onPaymentComplete}
                      setPaymentSuccess={setPaymentSuccess}
                      setupIntentID={setupIntent || localSetupIntentID}
                      claimData={claim}
                    />
                  ) : (
                    <>
                      {isPaymentElementLoading && (
                        <EmptyPage
                          PageIcon={FaCreditCard}
                          isLoading={isPaymentElementLoading}
                          title='Loading Payment'
                          subtitle='Please wait while we load your payment method.'
                        />
                      )}
                      <div
                        style={{
                          display: isPaymentElementLoading ? 'none' : 'block',
                        }}
                      >
                        <Payment
                          clientSecret={clientSecret}
                          returnUrl={`${window.location.href}`}
                          ref={paymentRef}
                          showButton={false}
                          onElementReady={handlePaymentElementReady}
                          onSetupSuccess={handleSetupSuccess}
                        >
                          <div className='flex flex-col gap-3 text-sm text-gray-500 md:hidden'>
                            <div className='mt-3'>
                              <span className='font-semibold'>
                                By clicking Pay Claim, you give permission to
                                ReLease to charge your provided payment method for the
                                amounts above
                              </span>
                              , and agree to ReLease's{' '}
                              <a
                                href='https://www.releaserent.com/legal/terms-of-service'
                                target='_blank'
                                rel='noopener noreferrer'
                                className='hover:underline'
                              >
                                Terms of Service
                              </a>{' '}
                              and{' '}
                              <a
                                href='https://www.releaserent.com/legal/privacy-policy'
                                target='_blank'
                                rel='noopener noreferrer'
                                className='hover:underline'
                              >
                                Privacy Policy
                              </a>
                              .
                            </div>
                          </div>
                        </Payment>
                      </div>
                    </>
                  )}
                </>
              )}

              {/* Show message when using saved payment method */}
              {!showNewCard && (
                <EmptyPage
                  PageIcon={FaCreditCard}
                  title='Using Saved Payment Method'
                  subtitle={
                    <div className='max-w-md w-full px-2'>
                      Click "Complete Payment" to finalize your claim using your saved payment method.
                    </div>
                  }
                />
              )}

              {/* Help Section */}
              <ContactCard className='pt-4 mt-8 border-t border-gray-200' border={false} title='Need help with your payment?'>
                <Text className='text-base text-gray-500'>
                  If you have any questions about your payment or need assistance, please reach out to our support team.
                </Text>
              </ContactCard>
            </div>
          </div>          
        </ApplyFormContainer>
      </ApplyContent>
    </div>
  );
};

export default Step2Payment; 